import { youtubeVideoIdFrom, type AdminExerciseVideoPin } from '@steado/admin-contract/exercises';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Panel } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';

import {
  exerciseVideoPinQueryOptions,
  pinExerciseVideo,
  unpinExerciseVideo,
} from './exercise-video-pin-query';

interface VideoPinPanelProps {
  readonly exerciseId: string;
}

const DESTRUCTIVE_OUTLINE = 'border-err-fg/25 text-err-fg hover:bg-err-bg hover:text-err-fg';

/**
 * Steado's pick: one YouTube video an operator chose, which the app shows
 * beside the search results rather than among them. The server checks the
 * video plays inside the app (public, embeddable, watchable in India, not
 * age-restricted) and says why when it does not.
 */
export function VideoPinPanel({ exerciseId }: VideoPinPanelProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const inputId = useId();
  const options = exerciseVideoPinQueryOptions(api, exerciseId);
  const pin = useQuery(options);
  const [text, setText] = useState('');
  const [isRemoving, setRemoving] = useState(false);

  const save = useMutation({
    mutationFn: () => pinExerciseVideo(api, exerciseId, text.trim()),
    onSuccess: (pinned) => {
      queryClient.setQueryData(options.queryKey, pinned);
      setText('');
    },
    // Each attempt spends a unit of the day's YouTube allowance.
    retry: false,
  });

  const remove = useMutation({
    mutationFn: () => unpinExerciseVideo(api, exerciseId),
    onSuccess: () => {
      queryClient.setQueryData(options.queryKey, null);
      setRemoving(false);
    },
    retry: false,
  });

  const typedId = text.trim() === '' ? null : youtubeVideoIdFrom(text);
  const disabledTitle = can.mutate ? undefined : 'Changing the library requires the admin role.';
  const current = pin.data ?? null;

  return (
    <Panel
      title="Steado’s pick (YouTube)"
      description="Shown in the app beside the YouTube results for this exercise, never inside them."
      action={
        current === null ? undefined : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setRemoving(true);
            }}
            disabled={!can.mutate || remove.isPending || save.isPending}
            title={disabledTitle}
            className={DESTRUCTIVE_OUTLINE}
          >
            Remove pick
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {pin.isPending ? (
          <p className="text-sm text-muted-foreground">Reading the pick…</p>
        ) : pin.isError ? (
          <p className="text-[13px] text-err-fg">Could not read the pick: {pin.error.message}</p>
        ) : current === null ? (
          <p className="text-sm text-muted-foreground">
            No pick. The app shows YouTube&apos;s results for the exercise&apos;s name alone.
          </p>
        ) : (
          <PinnedVideo pin={current} />
        )}

        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (typedId !== null) save.mutate();
          }}
        >
          <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
            {current === null ? 'Pin a video' : 'Replace the pick'}
          </Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={inputId}
              value={text}
              maxLength={300}
              placeholder="A YouTube link, or the video id"
              onChange={(event) => {
                setText(event.target.value);
                save.reset();
              }}
              disabled={!can.mutate}
              className="max-w-[420px] flex-1"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              disabled={!can.mutate || typedId === null || save.isPending}
              title={disabledTitle}
            >
              {save.isPending ? 'Checking with YouTube…' : 'Save pick'}
            </Button>
          </div>
          {text.trim() !== '' && typedId === null ? (
            <p className="text-[13px] text-muted-foreground">
              That is not a YouTube link or an eleven-character video id.
            </p>
          ) : null}
          {save.isError ? (
            <p role="alert" className="text-[13px] text-err-fg">
              {save.error.message}
            </p>
          ) : null}
        </form>
      </div>

      {isRemoving ? (
        <ConfirmDialog
          title="Remove Steado’s pick?"
          body={<p>The app stops showing it on the next open. YouTube&apos;s own results are untouched.</p>}
          confirmLabel="Remove pick"
          destructive
          isPending={remove.isPending}
          {...(remove.isError ? { error: remove.error.message } : {})}
          onConfirm={() => {
            remove.mutate();
          }}
          onCancel={() => {
            setRemoving(false);
          }}
        />
      ) : null}
    </Panel>
  );
}

function PinnedVideo({ pin }: { readonly pin: AdminExerciseVideoPin }) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const { video } = pin;
  const thumbnail = video?.thumbnailUrl ?? null;

  return (
    <div className="flex flex-wrap items-start gap-3">
      {thumbnail === null || thumbnailFailed ? null : (
        <img
          src={thumbnail}
          alt=""
          className="h-[90px] w-[160px] rounded-lg border border-border bg-muted object-cover"
          onError={() => {
            setThumbnailFailed(true);
          }}
        />
      )}
      <div className="flex min-w-0 flex-col gap-1">
        {video === null ? (
          <span className="text-sm text-muted-foreground">
            Its details are not cached; they are fetched again when the exercise is next opened.
          </span>
        ) : (
          <>
            <span className="text-sm font-medium text-foreground">{video.title}</span>
            <span className="text-[13px] text-muted-foreground">
              {video.channelTitle}
              {video.durationSeconds === null ? '' : ` · ${duration(video.durationSeconds)}`}
              {video.madeForKids ? ' · made for kids' : ''}
            </span>
          </>
        )}
        <span className="font-mono text-xs text-muted-foreground">{pin.videoId}</span>
      </div>
    </div>
  );
}

function duration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes)}:${String(rest).padStart(2, '0')}`;
}
